import axios from 'axios';

const TTL_MS = 10 * 60 * 1000;
const STORAGE_KEY = 'crewTimesheetActivitiesByProject';
const entries = new Map();
const inflight = new Map();
const listeners = new Set();

const notify = () => {
  listeners.forEach((fn) => fn());
};

const slimRows = (rows = []) =>
  (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      scopeId: row?.scopeId ?? row?.originalData?.scopeId,
      scope: row?.scope || row?.originalData?.scope || 'Unnamed Scope',
    }))
    .filter((row) => row.scope);

const readPersistedAll = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const persistOne = (projectId, rows) => {
  try {
    const all = readPersistedAll();
    all[projectId] = { rows: slimRows(rows), savedAt: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* ignore quota */
  }
};

const restorePersisted = () => {
  const all = readPersistedAll();
  Object.entries(all).forEach(([projectId, value]) => {
    const rows = slimRows(value?.rows);
    if (!projectId || rows.length === 0) return;
    entries.set(projectId, {
      status: 'ready',
      rows,
      error: null,
      fetchedAt: value?.savedAt || Date.now(),
    });
  });
};

restorePersisted();

export const subscribeCrewActivities = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const getCrewActivities = (projectId) => {
  const id = String(projectId || '').trim();
  if (!id) return null;
  return entries.get(id) || null;
};

export const hydrateCrewActivitiesFromRows = (projectId, rows) => {
  const id = String(projectId || '').trim();
  const nextRows = slimRows(rows);
  if (!id || nextRows.length === 0) return;
  entries.set(id, {
    status: 'ready',
    rows: nextRows,
    error: null,
    fetchedAt: Date.now(),
  });
  persistOne(id, nextRows);
  notify();
};

const loadOne = async (projectId, getAuthConfig) => {
  const id = String(projectId || '').trim();
  if (!id || typeof getAuthConfig !== 'function') return null;

  const existing = entries.get(id);
  if (existing?.status === 'ready' && Date.now() - (existing.fetchedAt || 0) < TTL_MS) {
    return existing;
  }
  if (inflight.has(id)) return inflight.get(id);

  if (!existing?.rows?.length) {
    entries.set(id, { status: 'loading', rows: [], error: null, fetchedAt: 0 });
    notify();
  }

  const promise = (async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_BASEURL;
      const res = await axios.get(
        `${base}/api/miter/production-performance/${encodeURIComponent(id)}/crew-timesheet-activities`,
        await getAuthConfig()
      );
      const rows = slimRows(res.data?.data?.rows);
      const next = { status: 'ready', rows, error: null, fetchedAt: Date.now() };
      entries.set(id, next);
      persistOne(id, rows);
      return next;
    } catch {
      const next = {
        status: existing?.rows?.length ? 'ready' : 'error',
        rows: existing?.rows || [],
        error: existing?.rows?.length ? null : 'Could not load activities.',
        fetchedAt: Date.now(),
      };
      entries.set(id, next);
      return next;
    } finally {
      inflight.delete(id);
      notify();
    }
  })();

  inflight.set(id, promise);
  return promise;
};

export const prefetchCrewActivities = (projectIds, getAuthConfig) => {
  const ids = [
    ...new Set((projectIds || []).map((id) => String(id || '').trim()).filter(Boolean)),
  ].slice(0, 10);
  ids.forEach((id) => {
    if (entries.get(id)?.status === 'ready') return;
    loadOne(id, getAuthConfig);
  });
};

export const ensureCrewActivities = (projectId, getAuthConfig) =>
  loadOne(projectId, getAuthConfig);
